import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/** Width preset for the modal panel. */
type ModalSize = "sm" | "md" | "lg" | "xl";

// ── Tailwind class maps (migrated from style/Modal.css) ──────────────
// All `rw-modal*` BEM markers are preserved as static class names
// because external consumers in header/ + features/ extend Modal via
// CSS descendant selectors targeting `.rw-modal__panel`, `.rw-modal__title`,
// `.rw-modal__footer`, etc. (see AnnotateScreenshotModal.tsx,
// SubmitTicketModal.tsx, CustomerDetailsModal.tsx). Stripping these
// names would break the consumers.

// `--color-bg-overlay` is the app's shared dimming-overlay token (see
// Table.tsx / DataTable.tsx's loading overlays) — near-black in dark
// theme, translucent white in light theme. The previous hardcoded navy
// rgba() barely darkened the page in dark mode (its luminance is close
// to the dark theme's own background), leaving the modal looking like
// it floated over an undimmed page.
const BACKDROP_CLASS =
  "rw-modal__backdrop fixed inset-0 flex items-center justify-center z-[2000] p-4 bg-[var(--color-bg-overlay)]";

const PANEL_BASE_CLASS = [
  "rw-modal__panel flex flex-col w-full outline-none",
  "bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]",
  "border border-[var(--color-border)]",
  "shadow-[var(--shadow-elevated)]",
  "max-h-[calc(100vh-32px)]",
].join(" ");

const PANEL_SIZE_CLASS: Record<ModalSize, string> = {
  sm: "rw-modal__panel--sm max-w-[380px]",
  md: "rw-modal__panel--md max-w-[560px]",
  lg: "rw-modal__panel--lg max-w-[760px]",
  xl: "rw-modal__panel--xl max-w-[1000px]",
};

const HEADER_CLASS =
  "rw-modal__header flex items-center justify-between gap-3 mx-[18px] py-3.5 border-b border-[var(--color-border)]";

// `flex 1 1 auto` + `min-w-0` + `overflow-wrap: anywhere` lets long
// unbreakable tokens (emails, URLs) wrap mid-token instead of pushing
// the × button off the panel.
const TITLE_CLASS =
  "rw-modal__title m-0 flex-auto min-w-0 [overflow-wrap:anywhere] text-base font-hk-semibold text-[var(--color-text-primary)]";

const CLOSE_BUTTON_CLASS = [
  "rw-modal__close flex-none self-start",
  // `mr-1` — the header's own right edge sits at the same `mx-[18px]` offset
  // as the scrollable body's right padding, so a browser scrollbar rendered
  // in the body (once content overflows) tracks almost flush against the
  // button's right edge with no visual buffer between them.
  "mr-1 border-0 bg-transparent text-[22px] leading-none cursor-pointer px-2 py-1 rounded-md",
  "text-[var(--color-text-secondary)]",
  "hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]",
].join(" ");

const BODY_CLASS = "rw-modal__body px-[18px] py-4 overflow-y-auto";

/**
 * Stack of currently-open modals' identity tokens, topmost last. Backs
 * the Escape handler and the focus trap below so a stacked modal (e.g. a
 * confirm dialog opened from within another Modal) only responds to the
 * modal actually on top — pressing Escape once must not cascade-close every
 * ancestor, and a nested modal's Tab trap must not fight an ancestor's.
 */
const OPEN_MODAL_STACK: object[] = [];

/**
 * Standard focusable-element selector for the Tab-trap below. Scoped to the
 * panel only — the modal previously had NO keyboard focus trap, so
 * Tab/Shift+Tab could escape the panel into the (visually obscured) page
 * behind it even though the backdrop already blocks mouse clicks from
 * reaching it.
 */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const FOOTER_CLASS =
  "rw-modal__footer flex items-center justify-end gap-2 px-[18px] py-3 border-t border-[var(--color-border)]";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Width preset: small (380), medium (560), wide (760), extra-wide (1000). */
  size?: ModalSize;
  /**
   * When true, clicking the backdrop/outside the modal closes it. Defaults to
   * false so form/confirm dialogs aren't dismissed by a stray click; opt in per
   * modal (e.g. CEO Pricing dialogs) where outside-click dismissal is wanted.
   */
  maskClosable?: boolean;
  /** Optional aria-label for the dialog when no `title` is supplied. */
  ariaLabel?: string;
  /**
   * Additional class applied to the BACKDROP element, so consumers can scope
   * theme tokens (e.g. `submit-ticket-modal`) without pierce-styling the
   * shared `.rw-modal__*` rules.
   */
  className?: string;
}

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  size = "md" as ModalSize,
  maskClosable = false,
  ariaLabel,
  className,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    // Own token per open→close lifecycle — lets the handler tell
    // "am I the topmost modal" apart from any other Modal instance
    // (or the same instance re-opening) without depending on `onClose`
    // reference identity.
    const token = {};
    OPEN_MODAL_STACK.push(token);
    const isTopmost = (): boolean =>
      OPEN_MODAL_STACK[OPEN_MODAL_STACK.length - 1] === token;
    // Keyboard focus trap: Tab/Shift+Tab wraps within the panel's own
    // focusable elements instead of escaping into the page behind the
    // modal. Mouse interaction was already blocked by the backdrop
    // (`onMouseDown` above) — this closes the same gap for keyboard-only
    // navigation.
    const trapTab = (e: KeyboardEvent): void => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusable.length === 0) {
        // No focusable descendant — keep focus pinned on the panel itself.
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !panel.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (!isTopmost()) return;
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab") {
        trapTab(e);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const idx = OPEN_MODAL_STACK.indexOf(token);
      if (idx !== -1) OPEN_MODAL_STACK.splice(idx, 1);
    };
  }, [open, onClose]);

  // Scroll-lock: body scroll is suspended while any Modal is open.
  // Restores whatever `overflow` value was in place before THIS modal
  // opened, so nested modals unwind correctly (LIFO) instead of one
  // modal's cleanup clobbering another's lock.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => previouslyFocused?.focus();
  }, [open]);

  const handleBackdropClick = useCallback((): void => {
    if (maskClosable) onClose();
  }, [maskClosable, onClose]);

  if (!open) return null;

  // Portal to `document.body` — mirrors every other floating primitive in
  // this design system (Tooltip, Select, SlideOverPanel, Popover,
  // DateRangePicker). Without it, a Modal mounted inside a cell of the
  // metadata-driven DataTable inherits that cell's own stacking context
  // (sticky columns/header set `position: relative` + an explicit
  // z-index on every `<td>`/`<th>`), so this backdrop's `z-[2000]` is only
  // compared against siblings WITHIN that cell's context — the panel can
  // render clipped/interleaved behind sticky cells or another row's
  // content instead of on top of the whole page.
  return createPortal(
    <div // NOSONAR — backdrop dismissal; close button inside panel handles keyboard access
      className={
        className === undefined
          ? BACKDROP_CLASS
          : `${BACKDROP_CLASS} ${className}`
      }
      onMouseDown={handleBackdropClick}>
      <div // NOSONAR — custom modal panel; native <dialog> showModal() does not integrate with scroll-lock + portal flow
        ref={panelRef}
        className={`${PANEL_BASE_CLASS} ${PANEL_SIZE_CLASS[size]}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : ariaLabel}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}>
        {title !== undefined ? (
          <header className={HEADER_CLASS}>
            <h2 className={TITLE_CLASS}>{title}</h2>
            <button
              type="button"
              className={CLOSE_BUTTON_CLASS}
              aria-label="Close"
              onClick={onClose}>
              ×
            </button>
          </header>
        ) : null}
        <div className={BODY_CLASS}>{children}</div>
        {footer !== undefined ? (
          <footer className={FOOTER_CLASS}>{footer}</footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
